import React from 'react'
import { createBrowserRouter, Navigate } from 'react-router-dom'
import Logout from '../pages/login/Logout'
import PublicPlotView from '../pages/plots/publicPlotView/PublicPlotView'
import PublicLayoutView from '../pages/projects/publicLayoutView/PublicLayoutView'
import RootLayout from '../layouts/RootLayout'
import AuthLayout from '../layouts/AuthLayout'
import AuthGuard from './AuthGuard'
import RouteGuard from './RouteGuard'
import { RouteErrorBoundary } from '../shared/errorBoundary/ErrorBoundary'

const LoginPage = React.lazy(() => import('../pages/login/Login'))
const MfaSetupPage = React.lazy(() => import('../pages/mfaSetup/MfaSetup'))
const VerifyTwoFactorPage = React.lazy(
  () => import('../pages/verifyTwoFactor/VerifyTwoFactor')
)
// const SignupPage = React.lazy(() => import('../pages/signup'))
const Dashboard = React.lazy(() => import('../pages/dashboard/Dashboard'))
const DeveloperProfilePage = React.lazy(
  () => import('../pages/developerProfile')
)
const ChangePassword = React.lazy(
  () => import('../pages/changePassword/ChangePassword')
)
const ProfilePage = React.lazy(() => import('../pages/profile'))
const BrandingPage = React.lazy(() => import('../pages/branding'))
const ProjectDetail = React.lazy(
  () => import('../pages/projects/projectDetail/ProjectDetail')
)
const PlotList = React.lazy(() => import('../pages/plots'))
const PlotDetail = React.lazy(
  () => import('../pages/plots/plotDetail/PlotDetail')
)
const LeadList = React.lazy(() => import('../pages/leads'))
const LeadDetail = React.lazy(
  () => import('../pages/leads/leadDetail/LeadDetail')
)
const QuoteList = React.lazy(() => import('../pages/quotes'))
const QuoteDetail = React.lazy(
  () => import('../pages/quotes/quoteDetail/QuoteDetail')
)
const BookingList = React.lazy(() => import('../pages/bookings'))
const BookingDetail = React.lazy(
  () => import('../pages/bookings/bookingDetail/BookingDetail')
)
const PaymentList = React.lazy(() => import('../pages/payments'))
const PaymentDetail = React.lazy(
  () => import('../pages/payments/paymentDetail/PaymentDetail')
)
const DocumentsBrowse = React.lazy(() => import('../pages/documents'))
const TaskList = React.lazy(() => import('../pages/tasks'))
const NotificationList = React.lazy(() => import('../pages/notifications'))
const ReportsHome = React.lazy(
  () => import('../pages/reports/reportsHome/ReportsHome')
)
const BrokerList = React.lazy(() => import('../pages/brokers'))
const CommunicationTemplateList = React.lazy(() =>
  import('../pages/communications').then((m) => ({ default: m.TemplateList }))
)
const CommunicationCalendar = React.lazy(() =>
  import('../pages/communications').then((m) => ({ default: m.CalendarPage }))
)
const CommunicationProjectUpdates = React.lazy(() =>
  import('../pages/communications').then((m) => ({
    default: m.CommunicationProjectUpdatesPage
  }))
)
const CommissionList = React.lazy(
  () => import('../pages/brokers/commissionList/CommissionList')
)
const CustomerList = React.lazy(() => import('../pages/customers'))
const CustomerDetail = React.lazy(
  () => import('../pages/customers/customerDetail/CustomerDetail')
)
const ModuleConfigPage = React.lazy(() => import('../pages/tenantConfig'))
const ModuleAdminPage = React.lazy(
  () => import('../pages/admin/modules/ModuleAdminPage')
)
const UserAdminListPage = React.lazy(() =>
  import('../pages/admin/users').then((m) => ({ default: m.UserAdminList }))
)
const UserPermissionsPage = React.lazy(() =>
  import('../pages/admin/users').then((m) => ({ default: m.UserPermissions }))
)
const RoleListPage = React.lazy(() =>
  import('../pages/admin/roles').then((m) => ({ default: m.RoleList }))
)
const RolePermissionsPage = React.lazy(() =>
  import('../pages/admin/roles').then((m) => ({ default: m.RolePermissions }))
)
const InviteSalesExec = React.lazy(() => import('../pages/invitations'))
const ForgotPassword = React.lazy(() => import('../pages/forgotPassword'))
const ResetPassword = React.lazy(() => import('../pages/resetPassword'))
const ActivateAccount = React.lazy(() => import('../pages/activateAccount'))
const TermsAndConditions = React.lazy(() => import('../pages/terms/TermsAndConditions'))
const ADMIN_ROLES = [
  'ROLE_DEVELOPER',
  'ROLE_ADMIN',
  'ROLE_SUPER_ADMIN',
  'ROLE_SALES_EXEC'
]

const appRouter = () =>
  createBrowserRouter([
    {
      element: <RootLayout />,
      // Catches route render errors (incl. a lazy route's ChunkLoadError) that a
      // data router routes here rather than to the app's class ErrorBoundary.
      errorElement: <RouteErrorBoundary />,
      children: [
        { path: '/', element: <Navigate to="/login" replace /> },
        { path: '/auth', element: <Navigate to="/login" replace /> },
        { path: '/login', element: <LoginPage /> },
        // Public by design: the user has no session yet, only an in-memory
        // enrolment token. MfaSetup self-redirects to /login when that is absent.
        { path: '/mfa-setup', element: <MfaSetupPage /> },
        // Returning-user 2FA challenge. Holds credentials in memory only and
        // self-redirects to /login when they are absent (e.g. a hard refresh).
        { path: '/verify-2fa', element: <VerifyTwoFactorPage /> },
        // Signup is intentionally disabled — accounts are provisioned via
        // invitation. Re-enable by uncommenting the route and the SignupPage import.
        // { path: '/signup', element: <SignupPage /> },
        { path: '/logout', element: <Logout /> },
        // Authenticated but intentionally OUTSIDE AuthLayout — renders the
        // full-screen AuthShell (no header / side nav / footer), same as login.
        {
          path: '/change-password',
          element: (
            <AuthGuard>
              <ChangePassword />
            </AuthGuard>
          )
        },
        { path: '/forgot-password', element: <ForgotPassword /> },
        { path: '/reset-password', element: <ResetPassword /> },
        { path: '/activate-account', element: <ActivateAccount /> },
        { path: '/terms', element: <TermsAndConditions /> },
        { path: '/plot/:token', element: <PublicPlotView /> },
        { path: '/layout/project/:token', element: <PublicLayoutView /> },
        {
          path: '/unauthorized',
          element: <h1>You don&apos;t have access to this page.</h1>
        },

        // Authenticated.
        {
          element: (
            <AuthGuard>
              <AuthLayout />
            </AuthGuard>
          ),
          children: [
            { path: '/dashboard', element: <Dashboard /> },
            { path: '/profile', element: <ProfilePage /> },
            {
              path: '/branding',
              element: (
                <RouteGuard
                  requiredRoles={['ROLE_ADMIN', 'ROLE_SUPER_ADMIN', 'ROLE_DEVELOPER']}
                >
                  <BrandingPage />
                </RouteGuard>
              )
            },
            {
              path: '/profile/developer',
              element: (
                <RouteGuard requiredRoles={['ROLE_DEVELOPER', 'ROLE_ADMIN']}>
                  <DeveloperProfilePage />
                </RouteGuard>
              )
            },
            {
              // The project LIST is retired — `/projects` now shows the detail
              // of the header-selected project directly (add/edit/delete live on
              // that page). ProjectDetail falls back to the selected project when
              // there's no :id in the URL.
              path: '/projects',
              element: (
                <RouteGuard requiredRoles={ADMIN_ROLES}>
                  <ProjectDetail />
                </RouteGuard>
              )
            },
            {
              path: '/projects/:id',
              element: (
                <RouteGuard requiredRoles={ADMIN_ROLES}>
                  <ProjectDetail />
                </RouteGuard>
              )
            },
            {
              path: '/projects/:projectId/plots',
              element: (
                <RouteGuard requiredRoles={ADMIN_ROLES}>
                  <PlotList />
                </RouteGuard>
              )
            },
            {
              path: '/plots',
              element: (
                <RouteGuard requiredRoles={ADMIN_ROLES}>
                  <PlotList />
                </RouteGuard>
              )
            },
            {
              // Project-scoped plot detail — preferred URL shape. Includes
              // the project id so a header project change is immediately
              // visible as a URL mismatch and the PlotDetail component
              // can react (redirect to the new project's plot list).
              path: '/projects/:projectId/plots/:id',
              element: (
                <RouteGuard requiredRoles={ADMIN_ROLES}>
                  <PlotDetail />
                </RouteGuard>
              )
            },
            {
              // Legacy unscoped URL — kept for back-compat. PlotDetail
              // internally redirects to the scoped form once the plot
              // loads and we know its projectId.
              path: '/plots/:id',
              element: (
                <RouteGuard requiredRoles={ADMIN_ROLES}>
                  <PlotDetail />
                </RouteGuard>
              )
            },
            {
              path: '/leads',
              element: (
                <RouteGuard requiredRoles={ADMIN_ROLES}>
                  <LeadList />
                </RouteGuard>
              )
            },
            {
              path: '/leads/:id',
              element: (
                <RouteGuard requiredRoles={ADMIN_ROLES}>
                  <LeadDetail />
                </RouteGuard>
              )
            },
            {
              path: '/quotes',
              element: (
                <RouteGuard requiredRoles={ADMIN_ROLES}>
                  <QuoteList />
                </RouteGuard>
              )
            },
            {
              path: '/quotes/:id',
              element: (
                <RouteGuard requiredRoles={ADMIN_ROLES}>
                  <QuoteDetail />
                </RouteGuard>
              )
            },
            {
              path: '/bookings',
              element: (
                <RouteGuard requiredRoles={[
                  'ROLE_DEVELOPER',
                  'ROLE_ADMIN',
                  'ROLE_SUPER_ADMIN',
                  'ROLE_SALES_EXEC',
                  'ROLE_ACCOUNTANT'
                ]}>
                  <BookingList />
                </RouteGuard>
              )
            },
            {
              path: '/bookings/:id',
              element: (
                <RouteGuard requiredRoles={[
                  'ROLE_DEVELOPER',
                  'ROLE_ADMIN',
                  'ROLE_SUPER_ADMIN',
                  'ROLE_SALES_EXEC',
                  'ROLE_ACCOUNTANT'
                ]}>
                  <BookingDetail />
                </RouteGuard>
              )
            },
            {
              path: '/payments',
              element: (
                <RouteGuard
                  requiredRoles={[
                    'ROLE_DEVELOPER',
                    'ROLE_ADMIN',
                    'ROLE_SUPER_ADMIN',
                    'ROLE_SALES_EXEC',
                    'ROLE_ACCOUNTANT'
                  ]}
                >
                  <PaymentList />
                </RouteGuard>
              )
            },
            {
              path: '/payments/:id',
              element: (
                <RouteGuard
                  requiredRoles={[
                    'ROLE_DEVELOPER',
                    'ROLE_ADMIN',
                    'ROLE_SUPER_ADMIN',
                    'ROLE_SALES_EXEC',
                    'ROLE_ACCOUNTANT'
                  ]}
                >
                  <PaymentDetail />
                </RouteGuard>
              )
            },
            {
              path: '/documents',
              element: (
                <RouteGuard requiredRoles={['ROLE_SUPER_ADMIN']}>
                  <DocumentsBrowse />
                </RouteGuard>
              )
            },
            {
              path: '/tasks',
              element: (
                <RouteGuard requiredRoles={ADMIN_ROLES}>
                  <TaskList />
                </RouteGuard>
              )
            },
            // /tasks/:id removed — task details are shown inline via a drawer
            // from the task list View action instead of a standalone page.
            { path: '/notifications', element: <NotificationList /> },
            {
              path: '/reports',
              element: (
                <RouteGuard
                  requiredRoles={[...ADMIN_ROLES, 'ROLE_ACCOUNTANT']}
                >
                  <ReportsHome />
                </RouteGuard>
              )
            },
            // All former per-report pages now live inside the single
            // /reports page — any old deep link redirects there.
            {
              path: '/reports/*',
              element: <Navigate to="/reports" replace />
            },
            {
              path: '/brokers',
              element: (
                <RouteGuard requiredRoles={ADMIN_ROLES}>
                  <BrokerList />
                </RouteGuard>
              )
            },
            {
              path: '/communications/templates',
              element: (
                <RouteGuard requiredRoles={ADMIN_ROLES}>
                  <CommunicationTemplateList />
                </RouteGuard>
              )
            },
            {
              path: '/communications/calendar',
              element: (
                <RouteGuard requiredRoles={ADMIN_ROLES}>
                  <CommunicationCalendar />
                </RouteGuard>
              )
            },
            {
              path: '/communications/project-updates',
              element: (
                <RouteGuard requiredRoles={ADMIN_ROLES}>
                  <CommunicationProjectUpdates />
                </RouteGuard>
              )
            },
            {
              path: '/commissions',
              element: (
                <RouteGuard requiredRoles={ADMIN_ROLES}>
                  <CommissionList />
                </RouteGuard>
              )
            },
            {
              path: '/customers',
              element: (
                <RouteGuard requiredRoles={ADMIN_ROLES}>
                  <CustomerList />
                </RouteGuard>
              )
            },
            {
              path: '/customers/:id',
              element: (
                <RouteGuard requiredRoles={ADMIN_ROLES}>
                  <CustomerDetail />
                </RouteGuard>
              )
            },
            {
              path: '/admin/module-config',
              element: (
                <RouteGuard requiredRoles={['ROLE_SUPER_ADMIN']}>
                  <ModuleConfigPage />
                </RouteGuard>
              )
            },
            {
              path: '/admin/modules',
              element: (
                <RouteGuard requiredRoles={['ROLE_SUPER_ADMIN']}>
                  <Navigate to="/admin/modules/PROJECT" replace />
                </RouteGuard>
              )
            },
            {
              path: '/admin/modules/:module',
              element: (
                <RouteGuard requiredRoles={['ROLE_SUPER_ADMIN']}>
                  <ModuleAdminPage />
                </RouteGuard>
              )
            },
            {
              path: '/admin/users',
              element: (
                <RouteGuard
                  requiredRoles={[
                    'ROLE_ADMIN',
                    'ROLE_SUPER_ADMIN',
                    'ROLE_DEVELOPER'
                  ]}
                >
                  <UserAdminListPage />
                </RouteGuard>
              )
            },
            {
              path: '/admin/users/:userId/permissions',
              element: (
                <RouteGuard
                  requiredRoles={['ROLE_ADMIN', 'ROLE_SUPER_ADMIN']}
                >
                  <UserPermissionsPage />
                </RouteGuard>
              )
            },
            {
              path: '/admin/roles',
              element: (
                <RouteGuard
                  requiredRoles={['ROLE_ADMIN', 'ROLE_SUPER_ADMIN']}
                >
                  <RoleListPage />
                </RouteGuard>
              )
            },
            {
              path: '/admin/roles/:roleCode/permissions',
              element: (
                <RouteGuard
                  requiredRoles={['ROLE_ADMIN', 'ROLE_SUPER_ADMIN']}
                >
                  <RolePermissionsPage />
                </RouteGuard>
              )
            },
            {
              path: '/invitations/sales-exec',
              element: (
                <RouteGuard requiredRoles={['ROLE_DEVELOPER']}>
                  <InviteSalesExec />
                </RouteGuard>
              )
            }
          ]
        },
        { path: '*', element: <Navigate to="/login" replace /> }
      ]
    }
  ])

export default appRouter
